import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Textarea,
  Label,
  useAuth,
  useToast,
} from '@org/ui';
import { Users, Plus } from 'lucide-react';

export const Route = createFileRoute('/spaces/')({
  component: SpacesPage,
});

function SpacesPage() {
  const { t } = useTranslation('community');
  const { user } = useAuth();
  const [showCreate, setShowCreate] = useState(false);

  const isAdmin =
    user?.permissions?.includes('admin:all') || user?.permissions?.includes('*');

  const { data, isLoading } = communityTrpc.spaces.list.useQuery({ limit: 50 });
  const spaces = data?.spaces ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">{t('spaces.title')}</h1>
        {isAdmin && (
          <Button onClick={() => setShowCreate(true)} className="gap-2">
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('spaces.newSpace')}
          </Button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-24" />
              </CardContent>
            </Card>
          ))
        ) : spaces.length === 0 ? (
          <div className="col-span-2 rounded-2xl border border-border bg-card p-8 text-center">
            <p className="text-muted-foreground">{t('spaces.noSpacesAvailable')}</p>
          </div>
        ) : (
          spaces.map((space) => (
            <Link key={space.spaceId} to="/spaces/$spaceId" params={{ spaceId: space.spaceId }}>
              <Card className="h-full transition-shadow hover:shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg">{space.name}</CardTitle>
                  {space.description && (
                    <CardDescription className="line-clamp-2">
                      {space.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{space.subscriberCount} {t('spaces.members')}</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))
        )}
      </div>

      {isAdmin && (
        <CreateSpaceDialog
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}

function CreateSpaceDialog({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('community');
  const { toast } = useToast();
  const utils = communityTrpc.useUtils();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');

  const createSpace = communityTrpc.spaces.create.useMutation({
    onSuccess: () => {
      toast({ title: t('spaces.createDialog.createdSuccess') });
      utils.spaces.list.invalidate();
      resetAndClose();
    },
    onError: (error) => {
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const resetAndClose = () => {
    setName('');
    setDescription('');
    setSlug('');
    onClose();
  };

  const handleSubmit = () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    createSpace.mutate({
      name: trimmedName,
      description: description.trim() || undefined,
      slug: slug.trim() || undefined,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && resetAndClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('spaces.createDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('spaces.createDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="space-name">
              {t('spaces.createDialog.nameLabel')} *
            </Label>
            <Input
              id="space-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('spaces.createDialog.namePlaceholder')}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="space-description">
              {t('spaces.createDialog.descriptionLabel')}
            </Label>
            <Textarea
              id="space-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('spaces.createDialog.descriptionPlaceholder')}
              rows={3}
              maxLength={500}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="space-slug">
              {t('spaces.createDialog.slugLabel')}
            </Label>
            <Input
              id="space-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder={t('spaces.createDialog.slugPlaceholder')}
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground">
              {t('spaces.createDialog.slugHint')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={resetAndClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || createSpace.isPending}
          >
            {createSpace.isPending
              ? t('spaces.createDialog.submitting')
              : t('spaces.createDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
